import { FC } from 'react';
import { z } from 'zod';
import { DrawerHeader } from '../../drawer/DrawerHeader';
import { Button, Stack } from '@mui/material';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { ActionFilters, actionStatusOptions, useActionFilters } from './useActionFilters';
import { ControlledFormField } from '../../form/ControlledFormField';
import { DrawerWrapper } from '../../drawer/DrawerWrapper';
import { DateRangePicker } from '../../form/DateRangePicker';
import { TagsCheckList } from '../../form/TagsCheckList';
import { OwnersCheckList } from '../../form/OwnersCheckList';
import { ActionStatusCheckList } from '../../form/ActionStatusCheckList';
import { PriorityCheckList, prioritiesOptions } from '../../form/PriorityCheckList';
import { JourneySelect } from '../../form/JourneySelect';

const actionFilterDrawerSchema = z.object({});

const createFormSchema = (intl: IntlShape) =>
	z.object({
		journeyId: z.string(),
		date: z
			.tuple([
				z
					.string()
					.datetime({
						offset: true,
					})
					.nullable(),
				z
					.string()
					.datetime({
						offset: true,
					})
					.nullable(),
			])
			.nullable(),
		deadline: z
			.tuple([
				z
					.string()
					.datetime({
						offset: true,
					})
					.nullable(),
				z
					.string()
					.datetime({
						offset: true,
					})
					.nullable(),
			])
			.nullable(),
		tags: z.array(z.string()),
		owners: z.array(z.string()),
		status: z.array(z.enum(actionStatusOptions)),
		priority: z.array(z.enum(prioritiesOptions)),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const dateRangeToTuple = (dateRange: ActionFilters['date']): [string, string] | undefined => {
	if (dateRange == null) {
		return undefined;
	}

	return [dateRange.start, dateRange.end];
};

const dateRangeFromTuple = (dateTuple: [string | null, string | null] | null): ActionFilters['date'] => {
	if (dateTuple?.[0] == null || dateTuple[1] == null) {
		return undefined;
	}

	return { start: dateTuple[0], end: dateTuple[1] };
};

const ActionFilterDrawer: FC = () => {
	const [filters, setFilters] = useActionFilters();
	const intl = useIntl();

	const form = useTypedForm({
		schema: createFormSchema(intl),
		defaultValues: {
			journeyId: filters.journeyId ?? '',
			date: dateRangeToTuple(filters.date) || null,
			deadline: dateRangeToTuple(filters.deadline) || null,
			tags: filters.tags ?? [],
			owners: filters.owners ?? [],
			status: filters.status ?? [],
			priority: filters.priority ?? [],
		},
	});

	const onSubmit = (values: FormValues) => {
		setFilters({
			journeyId: values.journeyId ? values.journeyId : undefined,
			date: dateRangeFromTuple(values.date),
			deadline: dateRangeFromTuple(values.deadline),
			tags: values.tags.length > 0 ? values.tags : undefined,
			owners: values.owners.length > 0 ? values.owners : undefined,
			status: values.status.length > 0 ? values.status : undefined,
			priority: values.priority.length > 0 ? values.priority : undefined,
		});
	};

	const onClearAll = () => {
		setFilters({});
		form.reset({ journeyId: '', date: null, tags: [], owners: [], status: [], priority: [] });
	};

	return (
		<form noValidate onSubmit={form.handleSubmit(onSubmit)} style={{ height: '100%' }}>
			<DrawerWrapper>
				<DrawerHeader title={<FormattedMessage id={'drawer.action-filter.title'} />} />
				<Stack width={'100%'} gap={1.5} height={'100%'} justifyContent={'space-between'}>
					<ControlledFormField
						form={form}
						name={'journeyId'}
						label={intl.formatMessage({ id: 'form.action-filter.date.label.journey' })}
						render={({ id, name, value, onChange }) => <JourneySelect id={id} name={name} value={value} onChange={onChange} />}
					/>
					<ControlledFormField
						form={form}
						name={'date'}
						label={intl.formatMessage({ id: 'form.action-filter.date.label.date' })}
						render={({ id, name, value, onChange }) => <DateRangePicker id={id} name={name} value={value} onChange={onChange} />}
					/>
					<ControlledFormField
						form={form}
						name={'deadline'}
						label={intl.formatMessage({ id: 'form.action-filter.date.label.deadline' })}
						render={({ id, name, value, onChange }) => <DateRangePicker id={id} name={name} value={value} onChange={onChange} />}
					/>
					<ControlledFormField
						form={form}
						name={'status'}
						label={intl.formatMessage({ id: 'form.action-filter.date.label.status' })}
						render={({ value, onChange }) => <ActionStatusCheckList value={value} onChange={onChange} />}
					/>
					<ControlledFormField
						form={form}
						name={'priority'}
						label={intl.formatMessage({ id: 'form.action-filter.date.label.priority' })}
						render={({ value, onChange }) => <PriorityCheckList value={value} onChange={onChange} size={'medium'} />}
					/>
					<ControlledFormField
						form={form}
						name={'tags'}
						label={intl.formatMessage({ id: 'form.action-filter.date.label.tags' })}
						render={({ value, onChange }) => <TagsCheckList value={value} onChange={onChange} size={'medium'} />}
					/>
					<ControlledFormField
						form={form}
						name={'owners'}
						label={intl.formatMessage({ id: 'form.action-filter.date.label.owner' })}
						render={({ value, onChange }) => <OwnersCheckList value={value} onChange={onChange} size={'medium'} />}
					/>

					<Stack flexGrow={1} direction={'row'} justifyContent={'end'} alignItems={'end'} gap={3}>
						<Button variant={'text'} onClick={onClearAll} color={'secondary'} sx={{ flexGrow: 1 }}>
							<FormattedMessage id={'form.action-filter.clear-all'} />
						</Button>
						<Button variant={'contained'} type={'submit'} color={'primary'} sx={{ flexGrow: 1 }}>
							<FormattedMessage id={'form.action-filter.apply'} />
						</Button>
					</Stack>
				</Stack>
			</DrawerWrapper>
		</form>
	);
};

export { actionFilterDrawerSchema, ActionFilterDrawer };
